<template>
    <div class="navShadow" :class="{ 'horizontal': effectiveHorizontal }"></div>
    <div id="nav" :class="{ 'horizontal': effectiveHorizontal, 'dark-theme': isDarkTheme }">
        <div class="nav-logo-wrapper" @click="$emit('toggle-drawer')">
            <div class="nav-logo" :class="{ 'dark': isDarkTheme }"></div>
        </div>
        <div class="menus" :class="{ 'has-more-button': showMoreButton }" ref="menusRef">
            <div class="mainMenuItem" v-if="gs.isLogin">
                <!-- 可見的選單項目（根據可用寬度動態計算） -->
                <a v-for="item in visibleMenuItems" :key="item.path || item.viewId"
                   :class="{ disabled: !item.hasAccess }"
                   @click="handleItemClick(item)">
                    <i :class="[item.icon]"></i>
                    <small>{{ item.title }}</small>
                </a>
            </div>
        </div>

        <!-- 更多選單按鈕（只在有溢出項目時顯示） -->
        <div v-if="showMoreButton" class="nav-more-button" @click="toggleMoreMenu">
            <i class="icofont-duotone icofont-menu"></i>
            <!-- 下拉選單 -->
            <div v-if="showMoreMenu" class="more-dropdown" @click.stop>
                <!-- 溢出的選單項目 -->
                <a v-for="item in overflowMenuItems" :key="'more-' + (item.path || item.viewId)"
                   :class="{ disabled: !item.hasAccess }"
                   @click="handleOverflowItemClick(item)">
                    <i :class="[item.icon]"></i>
                    <span>{{ item.title }}</span>
                </a>
                <!-- 設定選項（有溢出項目時放在選單裡） -->
                <a class="more-dropdown-settings" @click="openSettingsAndCloseMenu">
                    <i class="icofont-duotone icofont-duplicate-alt"></i>
                    <span>設定</span>
                </a>
            </div>
        </div>

        <!-- 時間顯示（根據空間動態決定是否顯示） -->
        <div v-if="effectiveHorizontal && gs.isLogin && !shouldHideDatetime" class="nav-datetime-info">
            <span v-if="showDate" class="nav-date">{{ currentDate }}</span>
            <span v-if="showTime" class="nav-time">{{ currentTime }}</span>
        </div>

        <!-- 設定按鈕（沒有「更多」時直接顯示） -->
        <div class="nav-settings-button" @click="openSystemSettings" v-if="gs.isLogin && !showMoreButton">
            <i class="icofont-duotone icofont-duplicate-alt"></i>
            <small>設定</small>
        </div>
    </div>
</template>

<script>
import { eventBus } from '@shared/stores/globalState';
import { sideNavMenus } from '@shared/config/menuConfig';
import userUIService from '@shared/services/userui';
import { localModes, parsePermissionKeyFromPath } from '@shared/config/pagePermissions';

export default {
    name: 'SideNav',
    props: {
        isLogin: {
            type: Boolean,
            required: true
        },
        isMobile: {
            type: Boolean,
            required: true
        },
        selectedOrgId: {
            type: String,
            default: ''
        },
        pagePermissions: {
            type: Array,
            default: () => []
        }
    },
    watch: {
        selectedOrgId: {
            handler(newVal) {
                if (newVal) {
                    this.loadPinnedTools();
                }
            },
            immediate: true
        }
    },
    emits: ['toggle-drawer', 'toggle-big-content', 'toggle-nav-orientation', 'toggle-nav-theme', 'open-system-settings'],
    data() {
        return {
            isHorizontal: true,
            isDarkTheme: false,
            showDate: true,
            showTime: true,
            currentTime: '',
            currentDate: '',
            timer: null,
            currentMode: 'operation', // 當前模式
            showMoreMenu: false, // 更多選單顯示狀態
            navWidth: 0, // 導覽列寬度（用於動態計算可見項目）
            resizeObserver: null, // ResizeObserver 實例
            // 菜單配置（從統一配置檔匯入）
            menuConfigs: sideNavMenus,
            // 釘選的工具（用於動態工具選單）
            pinnedTools: []
        };
    },
    computed: {
        // 小畫面強制使用橫向導覽
        effectiveHorizontal() {
            return this.isMobile || this.isHorizontal;
        },
        // 全部選單項目
        allMenuItems() {
            const staticItems = this.menuConfigs[this.currentMode] || [];

            // 如果是工具模式，動態添加釘選的工具
            if (this.currentMode === 'tools' && this.pinnedTools.length > 0) {
                const dynamicItems = this.pinnedTools.map(tool => ({
                    // 使用與 UserUIList 相同的方式
                    path: `/tools/UserUIRenderer?id=${tool.id}`,
                    dir: 'tools',
                    page: 'UserUIRenderer',
                    title: tool.name,
                    icon: 'icofont-wand',
                    uiId: tool.id,
                    viewId: `userui_${tool.shortId}`
                }));
                return [...staticItems, ...dynamicItems];
            }

            return staticItems;
        },
        // 處理選單項目（標記權限狀態，不過濾）
        processedMenuItems() {
            return this.allMenuItems.map(item => {
                const permKey = parsePermissionKeyFromPath(item.path);
                // 本地頁面（permKey 為 null）不需要權限
                const hasAccess = permKey === null || this.pagePermissions.includes(permKey);
                return { ...item, hasAccess };
            });
        },
        // 計算可顯示的項目數量
        visibleCount() {
            if (!this.effectiveHorizontal) return this.processedMenuItems.length;

            const logoWidth = 60;
            const itemWidth = 70;
            const moreButtonWidth = 60;
            // 日期時間根據 shouldHideDatetime 和實際配置決定是否佔用空間
            const datetimeWidth = this.shouldHideDatetime ? 0 : this.actualDatetimeWidth;

            // 先計算不含設定按鈕的可用空間（因為有「更多」按鈕時，設定會移到下拉選單中）
            const availableWidthWithoutSettings = this.navWidth - logoWidth - datetimeWidth;
            const totalItems = this.processedMenuItems.length;

            // 設定按鈕寬度（只有在全部項目都能顯示時才會佔用空間）
            const settingsWidth = 60;

            // 計算含設定按鈕的可用空間（用於判斷是否能全部顯示）
            const availableWidthWithSettings = availableWidthWithoutSettings - settingsWidth;

            // 檢查是否全部能顯示（需要設定按鈕空間）
            if (availableWidthWithSettings >= totalItems * itemWidth) {
                return totalItems; // 全部顯示
            }

            // 需要「更多」按鈕時，設定按鈕移到下拉選單中，不佔用空間
            // 計算預留「更多」按鈕空間後能顯示多少個
            const adjustedWidth = availableWidthWithoutSettings - moreButtonWidth;
            let maxWithMore = Math.floor(adjustedWidth / itemWidth);

            // 計算剩餘項目數量
            const remainingItems = totalItems - maxWithMore;

            // 如果剩餘項目較少（1-2個），檢查是否有足夠空間直接顯示所有項目（含設定按鈕）
            if (remainingItems <= 2) {
                const neededWidth = totalItems * itemWidth;
                if (availableWidthWithSettings >= neededWidth) {
                    // 有足夠空間顯示所有項目（含設定按鈕），不需要「更多」按鈕
                    return totalItems;
                }
            }

            // 使用「更多」按鈕的情況（設定按鈕移到下拉選單，不佔用空間）
            // 重新計算：不預留「更多」按鈕，看看能顯示多少個
            const maxWithoutMore = Math.floor(availableWidthWithoutSettings / itemWidth);

            // 如果所有項目都能顯示，就不需要「更多」按鈕（但還需要設定按鈕空間）
            if (maxWithoutMore >= totalItems && availableWidthWithSettings >= totalItems * itemWidth) {
                return totalItems;
            }

            // 計算實際使用的寬度（使用 maxWithMore）
            const usedWidth = maxWithMore * itemWidth + moreButtonWidth;
            const remainingWidth = availableWidthWithoutSettings - usedWidth;

            // 如果剩餘空間還能容納更多項目（至少能容納 1 個），就多顯示一些
            if (remainingWidth >= itemWidth) {
                const additionalItems = Math.floor(remainingWidth / itemWidth);
                maxWithMore = Math.min(totalItems, maxWithMore + additionalItems);
            } else if (remainingWidth >= itemWidth * 0.6 && maxWithMore < totalItems) {
                // 如果剩餘空間接近一個項目寬度（>= 70%），也嘗試顯示一個
                // 因為實際渲染時可能有一些彈性空間或間距
                maxWithMore = Math.min(totalItems, maxWithMore + 1);
            } else {
                // 如果剩餘空間接近 itemWidth（比如 >= itemWidth * 0.8），也嘗試顯示一個
                // 因為實際渲染時可能有一些彈性空間
                if (remainingWidth >= itemWidth * 0.8 && maxWithMore < totalItems) {
                    maxWithMore = Math.min(totalItems, maxWithMore + 1);
                }
            }

            // 最後檢查：如果 maxWithMore 已經接近 totalItems，且剩餘空間足夠（含設定按鈕），就全部顯示
            const finalRemainingItems = totalItems - maxWithMore;
            if (finalRemainingItems <= 2) {
                const neededWidth = totalItems * itemWidth;
                if (availableWidthWithSettings >= neededWidth) {
                    return totalItems;
                }
            }

            const finalCount = Math.max(0, maxWithMore);

            return finalCount;
        },
        // 可見的選單項目
        visibleMenuItems() {
            return this.processedMenuItems.slice(0, this.visibleCount);
        },
        // 溢出的選單項目（放進「更多」）
        overflowMenuItems() {
            return this.processedMenuItems.slice(this.visibleCount);
        },
        // 是否顯示「更多」按鈕（只在有溢出的選單項目時顯示）
        showMoreButton() {
            return this.effectiveHorizontal &&
                   this.gs.isLogin &&
                   this.overflowMenuItems.length > 0;
        },
        // 根據實際配置計算日期時間寬度
        actualDatetimeWidth() {
            if (!this.effectiveHorizontal || this.isMobile) return 0;
            let width = 0;
            if (this.showDate) width += 120; // 日期約 124px，用 120 計算
            if (this.showTime) width += 120; // 時間約 129px，用 120 計算
            return width;
        },
        // 是否應該隱藏日期時間來騰出空間
        shouldHideDatetime() {
            if (!this.effectiveHorizontal || this.isMobile) return true;
            if (!this.showDate && !this.showTime) return true;

            const logoWidth = 60;
            const itemWidth = 70;
            const settingsWidth = 60;

            const totalItems = this.processedMenuItems.length;
            const availableWithDatetime = this.navWidth - logoWidth - settingsWidth - this.actualDatetimeWidth;

            // 如果有日期時間時放不下全部項目，就隱藏日期時間
            return availableWithDatetime < totalItems * itemWidth;
        }
    },
    mounted() {
        // 從 localStorage 讀取狀態（預設為水平）
        const savedOrientation = localStorage.getItem('navOrientation');
        if (savedOrientation === 'vertical') {
            this.isHorizontal = false;
        }

        const savedTheme = localStorage.getItem('navTheme');
        if (savedTheme === 'dark') {
            this.isDarkTheme = true;
        }

        // 從 localStorage 讀取日期時間顯示設定
        const savedShowDate = localStorage.getItem('showDate');
        if (savedShowDate !== null) {
            this.showDate = savedShowDate === 'true';
        }

        const savedShowTime = localStorage.getItem('showTime');
        if (savedShowTime !== null) {
            this.showTime = savedShowTime === 'true';
        }

        // 從 localStorage 讀取模式
        const savedMode = localStorage.getItem('navMode');
        if (savedMode) {
            this.currentMode = savedMode;
        }

        // 初始化時間
        this.updateDateTime();
        this.timer = setInterval(this.updateDateTime, 1000);

        // 監聽外部的設定變化
        eventBus.on('system:nav-orientation-changed', this.handleOrientationChanged);
        eventBus.on('ui:theme-changed', this.handleThemeChanged);
        eventBus.on('show-date-change', this.handleShowDateChange);
        eventBus.on('show-time-change', this.handleShowTimeChange);
        eventBus.on('ui:nav-mode-change', this.handleModeChange);
        // 監聽釘選工具更新事件（用戶在工具列表中釘選/取消釘選時）
        eventBus.on('tools:pinned-updated', this.loadPinnedTools);

        // 設置 ResizeObserver 監聽導覽列寬度變化
        this.setupResizeObserver();
    },
    beforeUnmount() {
        if (this.timer) {
            clearInterval(this.timer);
        }
        eventBus.off('system:nav-orientation-changed', this.handleOrientationChanged);
        eventBus.off('ui:theme-changed', this.handleThemeChanged);
        eventBus.off('show-date-change', this.handleShowDateChange);
        eventBus.off('show-time-change', this.handleShowTimeChange);
        eventBus.off('ui:nav-mode-change', this.handleModeChange);
        eventBus.off('tools:pinned-updated', this.loadPinnedTools);

        // 清理 ResizeObserver
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        // 移除更多選單的點擊監聽
        document.removeEventListener('click', this.closeMoreMenu);
    },
    methods: {
        setupResizeObserver() {
            const navEl = document.getElementById('nav');
            if (!navEl) return;

            this.resizeObserver = new ResizeObserver(entries => {
                for (const entry of entries) {
                    this.navWidth = entry.contentRect.width;
                }
            });
            this.resizeObserver.observe(navEl);

            // 初始化寬度
            this.navWidth = navEl.offsetWidth;
        },
        openSystemSettings() {
            this.$emit('open-system-settings');
            eventBus.emit('ui:open-settings');
        },
        openFieldFilter() {
            eventBus.emit('ui:open-field-filter');
        },
        handleOrientationChanged(isHorizontal) {
            this.isHorizontal = isHorizontal;
        },
        handleThemeChanged(isDark) {
            this.isDarkTheme = isDark;
        },
        handleShowDateChange(show) {
            this.showDate = show;
        },
        handleShowTimeChange(show) {
            this.showTime = show;
        },
        handleModeChange(mode) {
            this.currentMode = mode;
        },
        updateDateTime() {
            const now = new Date();
            const timeString = now.toLocaleTimeString('en-US', {
                hour12: true,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
            // 將 AM/PM 移到前面
            const timeParts = timeString.split(' ');
            this.currentTime = timeParts[1] + ' ' + timeParts[0];
            this.currentDate = now.toLocaleDateString('zh-TW', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
        },
        setOrientation(isHorizontal) {
            this.isHorizontal = isHorizontal;
            localStorage.setItem('navOrientation', isHorizontal ? 'horizontal' : 'vertical');
            this.$emit('toggle-nav-orientation', this.isHorizontal);
            eventBus.emit('system:nav-orientation-change', this.isHorizontal);
        },
        setTheme(isDark) {
            this.isDarkTheme = isDark;
            localStorage.setItem('navTheme', isDark ? 'dark' : 'light');
            this.$emit('toggle-nav-theme', this.isDarkTheme);
            eventBus.emit('ui:theme-change', this.isDarkTheme);
        },
        openView(item) {
            // 關閉導航 Drawer
            eventBus.emit('ui:close-nav-drawer');

            // 檢查是否為特殊動作（如登出）
            if (item.path === 'logout') {
                eventBus.emit('ui:show-logout');
                return;
            }

            // 如果有 dir 和 page，使用 ui.openView 方式（如釘選的工具）
            if (item.dir && item.page) {
                this.ui.openView(item.dir, item.page, item.title, {
                    uiId: item.uiId,
                    viewId: item.viewId
                });
                return;
            }

            // 否則使用 eventBus 發送 view:open 事件
            eventBus.emit('view:open', {
                path: item.path,
                title: item.title,
                options: {}
            });
        },
        handleItemClick(item) {
            if (!item.hasAccess) {
                this.$Message?.info(`${item.title} 權限不足`);
                return;
            }
            this.openView(item);
        },
        handleOverflowItemClick(item) {
            if (!item.hasAccess) {
                this.$Message?.info(`${item.title} 權限不足`);
                return;
            }
            this.closeMoreMenu();
            this.openView(item);
        },
        toggleMoreMenu() {
            this.showMoreMenu = !this.showMoreMenu;
            if (this.showMoreMenu) {
                // 點擊外部關閉選單
                setTimeout(() => {
                    document.addEventListener('click', this.closeMoreMenu);
                }, 0);
            }
        },
        closeMoreMenu() {
            this.showMoreMenu = false;
            document.removeEventListener('click', this.closeMoreMenu);
        },
        openViewAndCloseMenu(item) {
            this.closeMoreMenu();
            this.openView(item);
        },
        openSettingsAndCloseMenu() {
            this.closeMoreMenu();
            this.openSystemSettings();
        },
        /**
         * 載入釘選的工具
         */
        async loadPinnedTools() {
            try {
                // 使用 prop 傳入的 selectedOrgId
                if (!this.selectedOrgId) {
                    return;
                }

                this.pinnedTools = await userUIService.getPinnedTools(this.selectedOrgId);
                console.log('[SideNav] 已載入釘選工具:', this.pinnedTools.length, '個');
            } catch (error) {
                console.error('[SideNav] 載入釘選工具失敗:', error);
                this.pinnedTools = [];
            }
        }
    }
};
</script>

<style scoped lang="stylus">
// Logo 容器（直向和橫向共用）
.nav-logo-wrapper
    width 60px
    height 60px
    overflow hidden
    margin 0
    border-bottom solid 1px #ddd
    float left
    position relative
    display flex
    align-items center
    justify-content center

#nav.horizontal .nav-logo-wrapper
    flex-shrink 0
    border-right solid 1px #ddd
    border-bottom none

.dark-theme .nav-logo-wrapper
    border-bottom solid 1px rgba(255, 255, 255, 0.2)
#nav.horizontal.dark-theme .nav-logo-wrapper
    border-right solid 1px rgba(255, 255, 255, 0.2)

// Logo（直向和橫向共用）
.nav-logo
    width 42px
    height 42px
    background-size contain
    background-image url('/shared/assets/logo_w.png')
    background-repeat no-repeat
    background-position center
    cursor pointer

    &.dark
        background-image url('/shared/assets/logo_d.png')

// 設定按鈕
.nav-settings-button
    display flex
    flex-direction column
    align-items center
    justify-content center
    gap 4px
    width 60px
    height 60px
    padding 8px 5px
    cursor pointer
    transition all 0.2s ease
    user-select none
    margin-top auto
    flex-shrink 0
    box-sizing border-box

    i
        font-size 24px
        flex-shrink 0
        width 24px
        height 24px
        display flex
        align-items center
        justify-content center
        color #888
        line-height 1
        transition all 0.2s ease

    small
        font-size 12px
        color #888
        text-align center
        transition all 0.2s ease

    &:hover
        background rgba(90, 159, 255, 0.1)

        i, small
            color #5A9FFF

// 深色主題下的設定按鈕（垂直）
#nav:not(.horizontal).dark-theme .nav-settings-button
    i, small
        color #fff

    &:hover
        background rgba(255, 255, 255, 0.1)

        i, small
            color #5A9FFF

// 橫向模式的設定按鈕
#nav.horizontal .nav-settings-button
    flex-direction column
    width 60px
    height 60px
    padding 0
    border-top none
    border-left 1px solid #e0e0e0

    i
        font-size 24px
        width 24px
        height 24px

    small
        display block
        font-size 12px

    &:hover
        background rgba(90, 159, 255, 0.1)

// 橫向深色主題的設定按鈕
#nav.horizontal.dark-theme .nav-settings-button
    border-left 1px solid rgba(255, 255, 255, 0.2)

    i
        color #fff

    &:hover
        background rgba(255, 255, 255, 0.1)

        i
            color #5A9FFF

// 橫向白色主題的設定按鈕
#nav.horizontal:not(.dark-theme) .nav-settings-button
    i
        color #888

    &:hover
        i
            color #5A9FFF

// 時間顯示元件（水平模式）
.nav-datetime-info
    display flex
    flex-direction row
    align-items center
    gap 20px
    justify-content flex-end
    padding 0 20px
    height 60px
    border-left 1px solid #e0e0e0

    .nav-date
        font-family 'Noto Sans TC', sans-serif
        font-size 16px
        font-weight 400
        color #333
        line-height 100%
        text-align right

    .nav-time
        font-family 'Noto Sans TC', sans-serif
        font-size 16px
        font-weight 400
        color #333
        line-height 100%
        text-align right

// 深色主題下的時間顯示
#nav.horizontal.dark-theme .nav-datetime-info
    border-left 1px solid rgba(255, 255, 255, 0.2)

    .nav-date, .nav-time
        color #fff

// 菜單組樣式（用於管理模式的子菜單）
.menu-group
    display flex
    flex-direction column
    margin 8px 0

.menu-group-title
    display flex
    align-items center
    gap 6px
    padding 8px 10px
    font-size 12px
    color #999
    font-weight 600
    text-transform uppercase
    cursor default

    i
        font-size 14px
        flex-shrink 0

    small
        flex 1
        overflow hidden
        text-overflow ellipsis
        white-space nowrap

// 主菜單項樣式（圖示上，文字下）- 與橫向模式統一
.mainMenuItem
    a
        display flex
        flex-direction column
        align-items center
        justify-content center
        gap 4px
        padding 8px 5px
        cursor pointer
        transition all 0.2s ease
        user-select none
        width 60px
        height 60px
        overflow hidden
        box-sizing border-box

        i
            font-size 24px
            flex-shrink 0
            width 24px
            height 24px
            display flex
            align-items center
            justify-content center
            color #888
            line-height 1

        small
            font-size 12px
            color #888
            text-align center
            overflow hidden
            text-overflow ellipsis
            white-space nowrap
            width 100%
            max-width 55px
            line-height 1.2

        &:hover
            background rgba(90, 159, 255, 0.1)

            i, small
                color #5A9FFF

        // 無權限項目樣式（灰色）
        &.disabled
            cursor not-allowed
            opacity 0.4

            i, small
                color #bbb

            &:hover
                background transparent

                i, small
                    color #bbb

// 深色主題下的菜單項
#nav:not(.horizontal).dark-theme .mainMenuItem a
    i, small
        color #fff

    &:hover
        background rgba(255, 255, 255, 0.1)

        i, small
            color #5A9FFF

    &.disabled
        opacity 0.4
        i, small
            color #666
        &:hover
            background transparent
            i, small
                color #666

// 更多選單按鈕（水平模式）
.nav-more-button
    display none

#nav.horizontal .nav-more-button
    display flex
    align-items center
    justify-content center
    width 60px
    height 60px
    cursor pointer
    transition all 0.2s ease
    border-left 1px solid #e0e0e0
    flex-shrink 0
    position relative

    i
        font-size 24px
        color #888
        transition color 0.2s ease
        display flex
        align-items center
        justify-content center
        width 24px
        height 24px
        line-height 1

    &:hover
        background rgba(90, 159, 255, 0.1)

        i
            color #5A9FFF

// 深色主題的更多按鈕
#nav.horizontal.dark-theme .nav-more-button
    border-left 1px solid rgba(255, 255, 255, 0.2)

    i
        color #fff

    &:hover
        background rgba(255, 255, 255, 0.1)

        i
            color #5A9FFF

// 下拉選單
.more-dropdown
    position absolute
    top 100%
    right 0
    background #fff
    border-radius 8px
    box-shadow 0 4px 20px rgba(0, 0, 0, 0.15)
    min-width 160px
    max-height 400px
    overflow-y auto
    z-index 99999
    padding 8px 0

    a
        display flex
        align-items center
        gap 12px
        padding 12px 16px
        color #333
        text-decoration none
        transition all 0.2s ease
        cursor pointer

        i
            font-size 18px
            color #666
            width 20px
            text-align center

        span
            font-size 14px

        &:hover
            background rgba(90, 159, 255, 0.1)

            i
                color #5A9FFF

        &.disabled
            opacity 0.4
            cursor not-allowed
            color #999
            i
                color #999
            &:hover
                background transparent
                i
                    color #999

// 深色主題的下拉選單
#nav.horizontal.dark-theme .more-dropdown
    background #1a3a5c
    box-shadow 0 4px 20px rgba(0, 0, 0, 0.3)

    a
        color #fff !important

        i
            color #aaa !important

        span
            color #fff !important

        &:hover
            background rgba(255, 255, 255, 0.1)

            i
                color #5A9FFF !important

            span
                color #5A9FFF !important

        &.disabled
            opacity 0.4
            color #666 !important
            i, span
                color #666 !important
            &:hover
                background transparent
                i, span
                    color #666 !important

// 設定選項（加分隔線）
.more-dropdown-settings
    border-top 1px solid #e0e0e0
    margin-top 8px
    padding-top 12px !important

#nav.horizontal.dark-theme .more-dropdown-settings
    border-top 1px solid rgba(255, 255, 255, 0.2)

// 橫向模式的菜單項（同樣圖示上，文字下）
#nav.horizontal .mainMenuItem a
    flex-direction column
    justify-content center
    align-items center
    gap 4px
    padding 0 10px
    height 60px
    border-bottom none
    border-right 1px solid #e0e0e0
    width 70px

    i
        font-size 24px
        width 24px
        height 24px
        display flex
        align-items center
        justify-content center
        color #888

    small
        font-size 12px
        color #888
        text-align center
        width 100%
        overflow hidden
        text-overflow ellipsis
        white-space nowrap

    &:hover
        background rgba(90, 159, 255, 0.1)

        i, small
            color #5A9FFF

    &.disabled
        opacity 0.4
        cursor not-allowed
        i, small
            color #bbb
        &:hover
            background transparent
            i, small
                color #bbb

// 深色主題下的橫向菜單項
#nav.horizontal.dark-theme .mainMenuItem a
    border-right-color rgba(255, 255, 255, 0.2)

    i, small
        color #fff

    &:hover
        background rgba(255, 255, 255, 0.1)

        i, small
            color #5A9FFF

    &.disabled
        opacity 0.4
        i, small
            color #666
        &:hover
            background transparent
            i, small
                color #666

// 子菜單項樣式
.menu-group-items
    display flex
    flex-direction column

    a
        padding 8px 10px 8px 40px
        cursor pointer
        transition all 0.2s ease
        user-select none
        font-size 12px
        border-bottom 1px solid transparent

        small
            display block

        &:hover
            background rgba(90, 159, 255, 0.05)

</style>